import { useState } from 'react';

import Image from 'next/image';

import { Grid, Typography, TextField, Button, colors, Alert } from '@material-ui/core';
import { makeStyles, withStyles } from '@material-ui/core/styles';

import PropTypes from 'prop-types';

import { SectionHeader, IconAlternate, TypedText } from '@/src/components/molecules';
import { CardProduct } from '@/src/components/organisms';
import useRDStation from '@/src/lib/rdstation/useRDStation';

import SectionDynamic from './SectionDynamic';

const FKTextField = withStyles({
  root: {
    '& .MuiInput-underline:before': {
      borderBottom: `1px solid ${colors.blueGrey[50]}`,
    },
    '& .MuiInput-underline:after': {
      borderBottom: `1px solid ${colors.indigo[200]}`,
    },
    '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
      borderBottom: `1px solid ${colors.indigo[200]}`,
    },
  },
})(TextField);

const useStyles = makeStyles(theme => ({
  typed: {
    fontWeight: 'bold',
  },
  listItemAvatar: {
    minWidth: 28,
  },
  listItemIcon: {
    marginTop: theme.spacing(1),
  },
  featuresContainer: {
    marginTop: theme.spacing(1),
  },
  formCover: {},
  cardProduct: {
    paddingTop: theme.spacing(4),
    [theme.breakpoints.up('md')]: {
      maxWidth: 400,
    },
  },
}));

export default function Contact(props) {
  const { className, section, image, title, subtitle, label, typed, features, refElem, ...rest } =
    props;

  const classes = useStyles();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [mobilePhone, setMobilePhone] = useState('');
  const { message, error, loading, subscribe } = useRDStation();

  const formattedTyped = typed.split('\n');
  const formattedFeatures = features.split('\n');
  const { url: imgSrc, alt: imgAlt } = image || {};

  const handleSubscribe = () => {
    subscribe({
      email,
      name,
      mobilePhone,
      conversionIdentifier: 'call',
      campaign: 'solutions',
      onSuccess: () => {
        setEmail('');
        setName('');
        setMobilePhone('');
      },
    });
  };

  return (
    <SectionDynamic type={section}>
      <div className={className} ref={refElem} {...rest}>
        <Grid container spacing={4}>
          <Grid
            item
            container
            justifyContent="flex-start"
            alignItems="center"
            xs={12}
            md={6}
            data-aos="fade-up">
            <CardProduct
              className={classes.cardProduct}
              withShadow
              liftUp
              mediaContent={
                imgSrc && <Image src={imgSrc} alt={imgAlt} layout="fill" objectFit="contain" />
              }
              cardContent={
                <Grid container spacing={4}>
                  <Grid item xs={12}>
                    <FKTextField
                      placeholder="Nome completo"
                      label="Nome completo *"
                      size="medium"
                      name="fullname"
                      fullWidth
                      type="text"
                      error={error}
                      disabled={loading}
                      onChange={evt => setName(evt.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FKTextField
                      placeholder="E-mail"
                      label="E-mail *"
                      size="medium"
                      name="email"
                      fullWidth
                      type="email"
                      error={error}
                      disabled={loading}
                      onChange={evt => setEmail(evt.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FKTextField
                      placeholder="Celular"
                      label="Celular *"
                      size="medium"
                      name="phone"
                      fullWidth
                      type="text"
                      error={error}
                      disabled={loading}
                      onChange={evt => setMobilePhone(evt.target.value)}
                      onKeyPress={evt => {
                        if (evt.key === 'Enter') {
                          handleSubscribe();
                          evt.preventDefault();
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      aria-label="Vamos conversar"
                      variant="contained"
                      color="primary"
                      fullWidth
                      size="large"
                      onClick={handleSubscribe}>
                      Vamos conversar
                    </Button>
                  </Grid>
                  {message && (
                    <Grid item xs={12}>
                      <Alert severity={error ? 'error' : 'info'}>{message}</Alert>
                    </Grid>
                  )}
                </Grid>
              }
            />
          </Grid>
          <Grid
            item
            container
            alignItems="center"
            justifyContent="flex-end"
            xs={12}
            md={6}
            data-aos="fade-up">
            <Grid item>
              <SectionHeader
                label={label}
                title={
                  <>
                    <span>{title}</span>
                    <br />
                    <TypedText
                      component="span"
                      variant="h4"
                      color="secondary"
                      className={classes.typed}
                      typedProps={{
                        strings: formattedTyped,
                        typeSpeed: 50,
                        loop: true,
                      }}
                    />
                  </>
                }
                subtitle={subtitle}
                align="left"
                disableGutter
              />
              <Grid container spacing={2} className={classes.featuresContainer}>
                {formattedFeatures.map((feature, index) => (
                  <Grid item sm={6} xs={12} key={index.toString()} data-aos="fade-up">
                    <Grid container spacing={1}>
                      <Grid item className={classes.listItemAvatar}>
                        <IconAlternate
                          size="extraSmall"
                          shape="circle"
                          fontIconClass="fas fa-check"
                          color={colors.green}
                          className={classes.listItemIcon}
                        />
                      </Grid>
                      <Grid item xs>
                        <Typography variant="subtitle1" color="secondary">
                          {feature}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </div>
    </SectionDynamic>
  );
}

Contact.propTypes = {
  className: PropTypes.string,
  section: PropTypes.string,
  image: PropTypes.object,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  label: PropTypes.string,
  typed: PropTypes.string,
  features: PropTypes.string,
  refElem: PropTypes.any,
};
